import { FlexSpinner } from '@lovoldsystem/ui'
import { DeleteOutline, EditOutlined } from '@mui/icons-material'
import DeleteWarningIcon from '@mui/icons-material/WarningAmberRounded'
import { Box, styled, Theme, Tooltip, Typography, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Context, useCallback, useMemo } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { Chip } from 'src/components/common/Chip'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DynamicTextTypography, DynamicTypography } from 'src/components/DynamicTextTypography'
import { RowMaintenanceTypeImageBox } from 'src/components/locality-dashboard/LocalityMaintenanceGrid'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import GenericTable from 'src/components/table/GenericTable'
import { useNavigation } from 'src/context/NavigationContext'
import { DeviationStatus, MaintenanceType } from 'src/enums'
import { permissionValue } from 'src/enums/permissionValue'
import { IDeviationType } from 'src/generated-types/deviation-type'
import { IScheduledServiceDeviation } from 'src/generated-types/locality-frequency'
import { useDialogCtx, useLocationCtx, useUserCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { deviationQueryKeys } from 'src/query-keys'
import { Padding } from 'src/styles/paddingComponent'
import { getMainComponentName, getMooringName } from 'src/utils/function'
import { deviationsEndpoints, invalidateDeviation, useDeleteDeviation, useGetDeviations } from '../../../api/deviation'
import { AppRoutesNavigation } from '../../../app-routes'
import NoDeviation from '../../../assets/svg/no-deviation.svg'
import { AuthorizationProvider, hasPermission } from '../../../components/common/Authorization'
import UpdateDeviationDialog from '../../../dialogs/deviation/UpdateDeviationDialog'
import { AuthorizationType } from '../../../enums/auth'
import { IDeviation, IDeviationFilters } from '../../../generated-types/deviation'
import { IDeviationPriority } from '../../../generated-types/deviation-priority'
import { getFormattedDate, getPageSize, getThemeColor } from '../../../utils'
import { getUserDetail, UserDetail } from '../../../utils/getUserDetails'

export interface IDeviationsTableProps {
  customerId: EntityId
  localityId?: EntityId
  mooringId?: EntityId
  positionId?: EntityId
  componentId?: EntityId
  mainComponentId?: EntityId
  localityFrequencyId?: EntityId
  localityFrequencyGroupId?: any
  columns?: GridColumns<IDeviation>
  disabled?: boolean
  queryParams?: any
  scheduledTaskId?: EntityId
  handleRowClick?: (cell: any) => void
  filterDeviation?: IDeviationFilters
  pop?: () => void
  autoHeight?: boolean
  height?: number
  rowsToFit?: { min?: number; max?: number; fill?: boolean }
  maxHeight?: number
  enablePagination?: boolean
  serviceStationId?: EntityId
  emptyStatusImageWidth?: string
  emptyStatusTitle?: string
  emptyStatusContent?: string
  emptyStatusTitleFontSize?: string
  emptyStatusContentFontSize?: string
  emptyStatusHeight?: string
  storageId?: EntityId
  selectedLocalityId?: EntityId
  isLocalityPortal?: boolean
}

export interface IDeviationTableContainerProps extends IDeviationsTableProps {
  updatePermissions: number[]
  deletePermissions: number[]
  permission?: number
  enableDelete?: boolean
  context?: Context<any> | null
  deviationActionButtons?: (deviation: IDeviation) => React.ReactNode
  checkDeleteButtonDisabled?: (deviation: IDeviation) => boolean
  checkEditButtonDisabled?: (deviation: IDeviation) => boolean
  maximumVisibleRowCount?: number
  minimalEmptyStatus?: boolean
}

const DeviationsDataGrid: React.FC<IDeviationsTableProps> = ({
  customerId,
  localityId,
  mooringId,
  positionId,
  componentId,
  mainComponentId,
  localityFrequencyId,
  localityFrequencyGroupId,
  columns,
  scheduledTaskId,
  handleRowClick,
  filterDeviation,
  autoHeight,
  height,
  rowsToFit,
  enablePagination = true,
  emptyStatusImageWidth,
  emptyStatusTitle,
  emptyStatusContent,
  emptyStatusTitleFontSize,
  emptyStatusContentFontSize,
}) => {
  const { data, isLoading } = useGetDeviations(
    customerId,
    localityId,
    mooringId,
    positionId,
    componentId,
    mainComponentId,
    localityFrequencyId,
    localityFrequencyGroupId,
    scheduledTaskId,
    0,
    1000,
    filterDeviation,
  )

  const { t } = useTranslation(['deviation', 'common'])
  const { activePortal } = useUserCtx()
  if (isLoading && !rowsToFit) return <FlexSpinner />

  return (
    <GenericTable
      id={`deviations-table-pagination-client-side-${activePortal}`}
      columns={columns!}
      getRowClassName={params =>
        params.indexRelativeToCurrentPage % 2 == 0 ? 'even hover-pointer' : 'odd hover-pointer'
      }
      data={(data?.items as IDeviation[]) ?? []}
      loading={isLoading}
      onCellClick={handleRowClick}
      getRowHeight={() => 'auto'}
      hideDefaultFooterPagination={true}
      enablePagination={enablePagination}
      autoHeight={autoHeight}
      height={height}
      rowsToFit={rowsToFit}
      noDataComponent={() => {
        return (
          <EmptyStatusCard
            image={NoDeviation}
            header={emptyStatusTitle ?? t('deviation-empty-status-title', { ns: 'deviation' })}
            content={emptyStatusContent ?? t('deviation-empty-status-content', { ns: 'deviation' })}
            imageWidth={emptyStatusImageWidth ?? '107px'}
            imageContainerClasses="p-2"
            contentTopMargin="10px"
            height={height?.toString()}
            contentFontSize={emptyStatusContentFontSize}
            headerFontSize={emptyStatusTitleFontSize}
            contentClasses="mt-2"
          />
        )
      }}
    />
  )
}

export const static_column = (
  t: TFunction<('common' | 'deviation')[], undefined>,
  theme: Theme,
  navigateTo?: (path: string) => void,
  customerId?: EntityId,
  localityId?: EntityId,
  pop?: () => void,
): GridColumns => [
  {
    field: 'id',
    headerName: t('id', { ns: 'common' }),
    minWidth: 80,
    maxWidth: 100,
    flex: 0,
  },
  {
    field: 'title',
    headerName: t('title', { ns: 'common' }),
    flex: 1,
    renderCell: ({ row }) => (
      <Box width={'100%'}>
        <DynamicTypography text={row.title} variant="body1" fontWeight={400} />
      </Box>
    ),
  },
  {
    field: 'createdBy',
    headerName: t('createdBy', { ns: 'common' }),
    minWidth: 170,
    valueGetter: ({ row }) => {
      return row.createdByUser ? getUserDetail(row.createdByUser, UserDetail.FullName) : '-'
    },
  },
  {
    field: 'description',
    headerName: t('description', { ns: 'common' }),
    flex: 1.5,
    renderCell: ({ row }) => (
      <Box width={'100%'}>
        <DynamicTypography text={row.description} variant="body1" fontWeight={500} />
      </Box>
    ),
  },
  {
    field: 'placement',
    headerName: t('placement', { ns: 'common' }),
    minWidth: 200,
    renderCell: ({ row }) => {
      const jsonPlacement = JSON.parse(row.placement)
      const placement = Object.entries(jsonPlacement).map(([key, value]) => {
        if (key === 'mooring' && value != null) {
          return [key, getMooringName(value as string)]
        }
        if (key === 'mainComponent' && value != null) {
          return [key, getMainComponentName(value as string)]
        }
        return [key, value]
      })
      const placementArray = placement
        .filter(([, item]) => item)
        .map(([, option]) => option)
        .reverse()

      if (jsonPlacement.positionDrawingNumber != null) {
        placementArray.splice(0, 1)
        return (
          <Typography width={'100%'}>
            <Tooltip title={placementArray.join(' <- ')}>
              <StyledDiv style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                {`#${jsonPlacement.positionDrawingNumber} (${jsonPlacement.position})`}
              </StyledDiv>
            </Tooltip>
          </Typography>
        )
      }
      return (
        <Box width={'100%'}>
          <DynamicTypography text={placementArray.join(' -> ')} />
        </Box>
      )
    },
  },
  {
    field: 'priority',
    headerName: t('priority', { ns: 'common' }),
    flex: 0.5,
    minWidth: 100,
    renderCell: value =>
      value.value == IDeviationPriority.Critical ? (
        <Chip
          label={t('critical', { ns: 'common' })}
          variant="filled"
          sx={{
            backgroundColor: getThemeColor(theme, MUIThemeColors.errorMain),
            color: 'white !important',
            height: '24px',
            borderRadius: '4px',
          }}
        />
      ) : (
        <Chip
          label={t('normal', { ns: 'common' })}
          variant="outlined"
          sx={{ borderColor: getThemeColor(theme, MUIThemeColors.secondaryLight), height: '24px', borderRadius: '4px' }}
        />
      ),
  },
  {
    field: 'origin',
    headerName: t('origin', { ns: 'common' }),

    renderCell: ({ row }) => {
      const deviation = row as IDeviation
      const scheduledServiceDeviations = deviation.scheduledServiceDeviations?.reduce(
        (deviations: IScheduledServiceDeviation[], dev) => {
          const existingItemIndex = deviations.findIndex(item => item.groupId === dev.groupId)

          if (existingItemIndex === -1) {
            deviations.push({ ...dev })
          } else {
            deviations[existingItemIndex].title +=
              deviations[existingItemIndex].mooringServiceId != dev.mooringServiceId ? ` | ${dev.title}` : ''
          }

          return deviations
        },
        [],
      )

      if (deviation?.maintenance?.name) {
        return (
          <>
            <Padding.pr4>{RowMaintenanceTypeImageBox({ type: MaintenanceType.DailyMaintenance })}</Padding.pr4>
            <DynamicTextTypography
              text={deviation.maintenance.name}
              onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                pop && pop()
                if (navigateTo && customerId && localityId) {
                  navigateTo(
                    `${AppRoutesNavigation.Localities.localityServices(customerId, localityId)}#maintenance-${
                      deviation.maintenance?.id
                    }`,
                  )
                }
              }}
              maxLength={12}
            />
          </>
        )
      }
      if (deviation?.maintenance?.name) {
        return (
          <>
            <Padding.pr4>{RowMaintenanceTypeImageBox({ type: MaintenanceType.DailyMaintenance })}</Padding.pr4>
            <DynamicTextTypography
              text={deviation.maintenance.name}
              onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                pop && pop()
                if (navigateTo && customerId && localityId) {
                  navigateTo(
                    `${AppRoutesNavigation.Localities.localityServices(customerId, localityId)}#maintenance-${
                      deviation.maintenance?.id
                    }`,
                  )
                }
              }}
              maxLength={12}
            />
          </>
        )
      }

      return scheduledServiceDeviations && scheduledServiceDeviations?.length != 0 ? (
        <>
          <Padding.pr4>{RowMaintenanceTypeImageBox({ type: MaintenanceType.Service })}</Padding.pr4>
          {scheduledServiceDeviations.map(localityFrequencyDeviation => (
            <DynamicTextTypography
              key={localityFrequencyDeviation.id}
              onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                pop && pop()
                if (navigateTo && customerId && localityId) {
                  if (localityFrequencyDeviation?.groupId) {
                    navigateTo(
                      AppRoutesNavigation.Localities.localityServiceGroup(
                        customerId,
                        localityId,
                        localityFrequencyDeviation?.groupId as string,
                      ),
                    )
                  } else
                    navigateTo(
                      AppRoutesNavigation.Localities.localityService(
                        customerId,
                        localityId,
                        localityFrequencyDeviation.scheduledServiceId,
                      ),
                    )
                }
              }}
              text={localityFrequencyDeviation?.title}
              maxLength={12}
            />
          ))}
        </>
      ) : deviation?.scheduledTask?.title && deviation.type === IDeviationType.Task ? (
        <>
          <Padding.pr4>{RowMaintenanceTypeImageBox({ type: MaintenanceType.Task })}</Padding.pr4>
          <DynamicTextTypography
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              pop && pop()
              if (navigateTo && customerId && localityId) {
                navigateTo(
                  AppRoutesNavigation.Localities.workOrder(
                    customerId,
                    localityId,
                    deviation?.scheduledTask?.id as EntityId,
                  ),
                )
              }
            }}
            text={deviation?.scheduledTask?.title}
            maxLength={12}
          />
        </>
      ) : (
        '-'
      )
    },
    minWidth: 150,
  },
  {
    field: 'status',
    headerName: t('status', { ns: 'common' }),
    renderCell: ({ row }) => {
      const inApproval = row.deviationStatus === DeviationStatus.InApproval
      const label = inApproval
        ? t('pending', { ns: 'deviation' })
        : !row.status
        ? t('open', { ns: 'common' })
        : t('closed', { ns: 'common' })
      return (
        <Chip
          label={label}
          variant={inApproval ? 'filled' : 'outlined'}
          sx={{
            ...(inApproval
              ? {
                  backgroundColor: getThemeColor(theme, MUIThemeColors.warningMain),
                  color: `${getThemeColor(theme, MUIThemeColors.white)} !important`,
                }
              : {
                  borderColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
                }),
            height: '26px',
            borderRadius: '4px',
            minWidth: inApproval ? 'auto' : '69px',
          }}
        />
      )
    },
    minWidth: 50,
  },
  {
    field: 'createdAt',
    headerName: t('registration-date', { ns: 'deviation' }),
    flex: 0.6,
    minWidth: 100,
    valueGetter: value => {
      return getFormattedDate(new Date(value.value))
    },
  },
  {
    field: 'dueDate',
    headerName: t('dueDate', { ns: 'common' }),
    flex: 0.6,
    minWidth: 100,
    valueGetter: value => {
      return getFormattedDate(new Date(value.value))
    },
  },
]

export const DeviationsTable: React.FC<IDeviationTableContainerProps> = ({
  customerId,
  localityId,
  mooringId,
  positionId,
  componentId,
  mainComponentId,
  localityFrequencyId,
  scheduledTaskId,
  disabled,
  queryParams,
  handleRowClick,
  updatePermissions,
  filterDeviation,
  pop,
  autoHeight,
  height,
  rowsToFit,
  enablePagination = true,
  deletePermissions,
  permission,
  enableDelete = false,
  context,
  serviceStationId,
  storageId,
  emptyStatusHeight,
  emptyStatusImageWidth,
  emptyStatusContent,
  emptyStatusTitle,
  emptyStatusContentFontSize,
  emptyStatusTitleFontSize,
  selectedLocalityId,
  deviationActionButtons,
  checkDeleteButtonDisabled,
  checkEditButtonDisabled,
}) => {
  const { showDialog, showConfirmDialog, closeAllDialogs } = useDialogCtx()
  const { navigateTo } = useNavigation()
  const theme = useTheme()
  const { t } = useTranslation(['deviation', 'common'])
  const { permission: p } = useLocationCtx()
  const isClientAdmin = hasPermission([permissionValue.ClientAdminPermission], p)

  const mutation = useDeleteDeviation(customerId)
  const onDelete = (id: EntityId) => {
    showConfirmDialog(
      t('deleteDeviation'),
      t('areYouSureDeleteDeviation'),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      pop => {
        mutation.mutate(id, {
          onSuccess: () => {
            toast.success(t('deviationDeletedSuccessfully'))
            invalidateDeviation.getDeviationsAdmin()
            invalidateDeviation.invalidateAllDeviations(customerId, localityId, undefined, undefined)
            invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
            invalidateCustomer.getCustomersActivityCounts(customerId)
            invalidationActivityLogCache.getCageActivityLogs()
            invalidateMainComponentCache.getMainComponents(customerId as EntityId, localityId as EntityId)
            pop()
          },
        })
      },
      {
        Icon: (
          <DeleteWarningIcon
            sx={{ height: '43px', width: '48px', color: theme.palette.errorMain[theme.palette.mode] }}
          />
        ),
        iconBGColor: '#D32F2F4D',
        buttonTextColor: theme.palette.white[theme.palette.mode],
        buttonBGColor: theme.palette.errorMain[theme.palette.mode],
      },
      true,
    )
  }

  const updateDeviation = useCallback(
    async (deviation: IDeviation) => {
      const updated = await showDialog(
        UpdateDeviationDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId,
            deviation: deviation,
            selectedLocalityId: selectedLocalityId,
            permission: p,
          },
        },
        undefined,
        true,
      )
      if (!updated) return
    },
    [customerId, localityId, selectedLocalityId, p, showDialog],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...static_column(t, theme, navigateTo, customerId, localityId as EntityId, pop),
      {
        field: 'actions',
        headerName: '',
        minWidth: 100,
        renderCell: ({ id, row }) => (
          <Padding.p6
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-evenly'}
            width={100}
            marginBottom={'2px'}
          >
            {!(checkEditButtonDisabled && checkEditButtonDisabled(row)) && (
              <AuthorizationProvider
                Context={context}
                permissions={updatePermissions}
                type={AuthorizationType.Disabled}
              >
                <GridActionButton
                  disabled={disabled ? disabled : row.status}
                  onClick={e => {
                    e.stopPropagation()
                    updateDeviation(row)
                  }}
                  buttonIcon={<EditOutlined />}
                  width="32px"
                  data-cy="UpdateDeviation"
                  height="32px"
                  shadowWeight={0}
                />
              </AuthorizationProvider>
            )}
            {enableDelete && !(checkDeleteButtonDisabled && checkDeleteButtonDisabled(row)) && (
              <AuthorizationProvider
                Context={context}
                permissions={deletePermissions}
                type={AuthorizationType.Disabled}
                permission={permission}
              >
                <GridActionButton
                  disabled={disabled || (row.status && !isClientAdmin)}
                  key={'delete'}
                  buttonIcon={<DeleteOutline />}
                  onClick={e => {
                    e.stopPropagation()
                    onDelete(id)
                  }}
                  width="32px"
                  data-cy="UpdateDeviation"
                  height="32px"
                  shadowWeight={0}
                />
              </AuthorizationProvider>
            )}
            {deviationActionButtons && deviationActionButtons(row)}
          </Padding.p6>
        ),
      },
    ],
    [
      disabled,
      t,
      updateDeviation,
      updatePermissions,
      deviationActionButtons,
      checkDeleteButtonDisabled,
      checkEditButtonDisabled,
    ],
  )

  const onRowClick = useCallback(
    cell => {
      closeAllDialogs()
      if (
        disabled ||
        (permission &&
          !hasPermission(
            [
              ...updatePermissions,
              ...deletePermissions,
              permissionValue.Locality.MainComponent.Deviation.Read,
              permissionValue.Locality.Deviation.Read,
            ],
            permission,
          ))
      )
        return
      if (selectedLocalityId || localityId)
        navigateTo(
          AppRoutesNavigation.Localities.deviation(customerId, (selectedLocalityId ?? localityId) as EntityId, cell.id),
        )
      else if (serviceStationId)
        navigateTo(AppRoutesNavigation.serviceStation.deviation(customerId, serviceStationId as EntityId, cell.id))
      else if (storageId) {
        navigateTo(AppRoutesNavigation.storage.deviation(customerId, storageId as EntityId, cell.id))
      } else navigateTo(AppRoutesNavigation.Customer.deviation(customerId, cell.id))
    },
    [customerId, disabled, localityId, navigateTo],
  )

  if (mooringId || positionId || componentId || mainComponentId || localityFrequencyId || scheduledTaskId) {
    return (
      <DeviationsDataGrid
        customerId={customerId}
        localityId={localityId}
        mooringId={mooringId}
        positionId={positionId}
        componentId={componentId}
        mainComponentId={mainComponentId}
        localityFrequencyId={localityFrequencyId}
        scheduledTaskId={scheduledTaskId}
        columns={columns}
        disabled={disabled}
        handleRowClick={handleRowClick ?? onRowClick}
        enablePagination={enablePagination}
        autoHeight={autoHeight}
        height={height}
        rowsToFit={rowsToFit}
        filterDeviation={filterDeviation}
        emptyStatusContent={emptyStatusContent}
        emptyStatusTitle={emptyStatusTitle}
        emptyStatusHeight={height?.toString()}
        emptyStatusImageWidth={emptyStatusImageWidth}
        emptyStatusContentFontSize={emptyStatusContentFontSize}
        emptyStatusTitleFontSize={emptyStatusTitleFontSize}
      />
    )
  }

  return (
    <GenericTable
      id={'deviations-table-pagination-server-side'}
      queryUrl={deviationsEndpoints.getDeviations(customerId)}
      generateQueryKey={(page: number, pageSize: number) =>
        deviationQueryKeys.GET_DEVIATIONS(customerId, {
          ...queryParams,
          page,
          pageSize,
          ...filterDeviation,
        })
      }
      getRowClassName={params =>
        params.indexRelativeToCurrentPage % 2 == 0 ? 'even hover-pointer' : 'odd hover-pointer'
      }
      enableServerPagination
      hideDefaultFooterPagination
      pageSize={height ? getPageSize(height / 44) : undefined}
      queryParams={{ ...queryParams, ...filterDeviation }}
      rowTopSpacing={0}
      onRowClick={handleRowClick ?? onRowClick}
      columns={columns}
      noRowsMessage={t('localityHasNoDeviations', { ns: 'deviation' })}
      enablePagination={enablePagination}
      autoHeight={autoHeight}
      height={height}
      rowsToFit={rowsToFit}
      noDataComponent={() => {
        return (
          <Box p={1} className="text-center">
            <EmptyStatusCard
              image={NoDeviation}
              header={t('deviation-empty-status-title', { ns: 'deviation' })}
              content={t('deviation-empty-status-content', { ns: 'deviation' })}
              imageWidth={emptyStatusImageWidth ?? '162px'}
              imageContainerClasses="p-4"
              contentTopMargin="10px"
              height={emptyStatusHeight}
            />
          </Box>
        )
      }}
    />
  )
}

const StyledDiv = styled('div')(() => ({
  cursor: 'pointer',
  fontSize: 'inherit',
}))
